import { useCoinBasis } from '@/hooks/coin'
import { useEffect, useMemo } from 'react'
import { useTranslation } from '@/services/i18n'
import {
  FieldRuleMap,
  useFeeValidate,
  FieldKeys,
  CustomizeFeeRateProps,
} from '@/hooks/fee/validator'
import {
  genInt,
  genIntOrDouble,
  genMaxOrEqualRule,
  genMinOrEqualRule,
  genMinRule,
  precision,
  RuleItem,
} from '@/utils/validator'

export const useAptosFeeRules = (dto: CustomizeFeeRateProps) => {
  const { coinKey, mini, isSpeed, max } = dto

  const { t } = useTranslation()
  const coinBasis = useCoinBasis(coinKey)

  const baseRule: RuleItem[] = [genIntOrDouble(), precision(coinBasis?.feeDecimal || 0)]
  const miniValueRule = useMemo<RuleItem | null>(
    () => (mini ? genMinOrEqualRule(mini.gasUnitPrice!, t('fee.gasUnitPriceLower')) : null),
    [mini]
  )

  // ----------- gasUnitPrice rules --------------
  const gasUnitPriceforceRules = useMemo<RuleItem[]>(() => {
    // https://aptos.dev/concepts/base-gas/#payload-gas
    const tmp = [
      ...baseRule,
      genMinOrEqualRule(100, t('fee.gasUnitPriceGreaterAndEqualThan', { x: 100 })),
      genMaxOrEqualRule(10000000000, t('fee.gasUnitPriceLessAndEqualThan', { x: 10000000000 })),
    ]
    if (isSpeed && miniValueRule) {
      tmp.push(miniValueRule)
    }
    return tmp
  }, [coinBasis, miniValueRule])
  const gasUnitPriceWeakRules = useMemo<RuleItem[]>(() => {
    const rules: RuleItem[] = []
    if (miniValueRule && !isSpeed) {
      rules.push(miniValueRule)
    }
    if (max) {
      rules.push(genMaxOrEqualRule(max.gasUnitPrice!, t('fee.gasUnitPriceHigher')))
    }
    return rules
  }, [mini?.gasUnitPrice, max?.gasUnitPrice, miniValueRule])

  const fieldRuleMap: FieldRuleMap = {
    gasUnitPrice: {
      force: gasUnitPriceforceRules,
      weak: gasUnitPriceWeakRules,
    },
    maxGasAmount: {
      force: [...baseRule, genInt(), genMinRule(0, t('fee.maxGasAmountGreaterThan0'))],
    },
  }

  return fieldRuleMap
}

export const useValidateAptosFeeFields = (dto: CustomizeFeeRateProps) => {
  const { value, onChangeValue } = dto

  const fieldRuleMap = useAptosFeeRules(dto)

  const [errorMap, doValidate] = useFeeValidate()

  // ----------- validate service --------------
  const validteAndEmitChange = () => {
    const formIsError = doValidate(value, fieldRuleMap)
    onChangeValue && onChangeValue(formIsError, value)
  }

  useEffect(() => validteAndEmitChange(), [value])

  const emitChange = <T extends FieldKeys>(fieldKey: T, text: string) => {
    const newValue = { ...value, [fieldKey]: text }
    onChangeValue && onChangeValue(true, newValue)
  }

  const handleChangeGasUnitPrice = (text: string) => emitChange('gasUnitPrice', text)
  const handleChangeMaxGasAmount = (text: string) => emitChange('maxGasAmount', text)

  return {
    errorMap,
    handleChangeGasUnitPrice,
    handleChangeMaxGasAmount,
  }
}
